/**
 * `next/link` compatibility shim.
 *
 * Maps Next.js's `<Link href=...>` API onto react-router-dom's `<Link to=...>`.
 * External URLs (anything starting with `http`, `mailto:`, `tel:`, `#`) and
 * any link with `target` / `download` / `rel` flagged for external use fall
 * through to a plain `<a>` so router navigation isn't attempted on them.
 *
 * Files migrated from the Next.js webapp keep their original
 *   `import Link from 'next/link'`
 * lines; Vite's resolve.alias rewrites that to point here.
 */
import { forwardRef } from 'react';
import type { AnchorHTMLAttributes, ReactNode, Ref } from 'react';
import { Link as RouterLink } from 'react-router-dom';

interface NextLinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  href: string;
  children?: ReactNode;
  // Next.js Link API extras — accepted and ignored.
  prefetch?: boolean | null;
  scroll?: boolean;
  replace?: boolean;
  shallow?: boolean;
  passHref?: boolean;
  locale?: string | false;
  legacyBehavior?: boolean;
}

function isExternalHref(href: string): boolean {
  return (
    href.startsWith('http://') ||
    href.startsWith('https://') ||
    href.startsWith('mailto:') ||
    href.startsWith('tel:') ||
    href.startsWith('//')
  );
}

const Link = forwardRef<HTMLAnchorElement, NextLinkProps>(function Link(
  { href, children, replace, prefetch: _prefetch, scroll: _scroll, shallow: _shallow, passHref: _passHref, locale: _locale, legacyBehavior: _legacyBehavior, target, rel, download, ...rest },
  ref
) {
  if (isExternalHref(href) || target === '_blank' || download !== undefined) {
    return (
      <a ref={ref} href={href} target={target} rel={rel ?? (target === '_blank' ? 'noopener noreferrer' : undefined)} download={download} {...rest}>
        {children}
      </a>
    );
  }

  return (
    <RouterLink ref={ref as Ref<HTMLAnchorElement>} to={href} replace={replace} target={target} rel={rel} {...rest}>
      {children}
    </RouterLink>
  );
});

export default Link;
